import { useCallback, useEffect, useMemo } from 'react';

import { getBrowserBridgeAPI, type ClientId } from '@atlassian/rovo-content-bridge-api';
import {
	ROVO_CHAT_CLIENT_ID,
	getConfluenceEditorClientId,
	AifcExperienceTrackingCommand,
	type AifcExperienceTrackingCommandProperties,
} from '@atlassian/rovo-content-bridge-api-commands';
import {
	ExperienceName as RovoExperienceName,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import { extractContentFeatures } from '@atlassian/conversation-assistant';

import { fg } from '@confluence/feature-gating';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';

import { log } from '../utils/aifc-logging';
import { getDocument } from '../utils/bridge-document-utils';
import { useIsPageViewMode } from '../utils/useIsPageViewMode';
import type {
	ErrorCallbackParams,
	PassThroughCommandProperties,
	StreamCallback,
	StreamCallbackParams,
	StreamEndCallbackParams,
	StreamStartCallbackParams,
} from '../command-handlers/types/command-handler-types';
import { RovoBridgeCommandManager } from '../bridge-management/rovo-bridge-command-manager';
import type { UseRovoBridgeCommandListenerParams } from '../RovoBridgeCommandListener';

import { useMediaImageProcessor } from './useMediaImageProcessor';

const scheduleTeardown = (teardown: () => void) => {
	const run = () => {
		/**
		 * @description We do NOT want to yield a return value here. It can cause certain unload
		 * handlers to display a window alert with "Are you sure you want to leave this page?".
		 * Specifically, see @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event,
		 * which is remarked to be a cancellable event by browsers.
		 */
		teardown();
		// only needs to run once - the JS context is gone after this, so, unneeded afterward
		unmount();
	};

	const unmount = () => {
		// Although unnecessary (the JS context is being destroyed), we remove the event listeners
		// to avoid any potential memory leaks due to hanging references - side effects can cause this.
		window.removeEventListener('unload', run);
		window.removeEventListener('pagehide', run);
	};

	// Below, we attempt to teardown on different event handlers which we know will trigger
	// on complete unmount of the document in this JS context. Note: Same-origin iframes are
	// indeed considered their own JS context here; just that they have access to other contexts.
	// Browsers report `unload` and `beforeunload` to be unreliable. Hence, we leverage a chain
	// of these events. See official MDN documentation for more (linked below).

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
	 * TODO - bring beforeunload back by consolidating handlers per discussion at -
	 * https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/207747/overview#comment-674441389
	 */
	// window.addEventListener('beforeunload', run, { once: true });
	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
	 */
	window.addEventListener('unload', run, { once: true });
	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/pagehide_event
	 */
	window.addEventListener('pagehide', run, { once: true });
};

const sendExperienceTrackingToParent = (
	props: Omit<AifcExperienceTrackingCommandProperties, 'senderId' | 'receiverId'>,
	commandParams: { senderId: ClientId },
) => {
	try {
		const api = getBrowserBridgeAPI();
		let payload: AifcExperienceTrackingCommandProperties;

		switch (props.status) {
			case 'fail':
				payload = {
					...props,
					error: typeof props.error === 'string' ? props.error : 'Unknown error',
					reason: undefined,
					senderId: commandParams.senderId,
					receiverId: ROVO_CHAT_CLIENT_ID,
				} as AifcExperienceTrackingCommandProperties;
				break;
			case 'abort':
				payload = {
					...props,
					reason: typeof props.reason === 'string' ? props.reason : 'Aborted',
					error: undefined,
					senderId: commandParams.senderId,
					receiverId: ROVO_CHAT_CLIENT_ID,
				} as AifcExperienceTrackingCommandProperties;
				break;
			case 'success':
			default:
				payload = {
					...props,
					error: undefined,
					reason: undefined,
					senderId: commandParams.senderId,
					receiverId: ROVO_CHAT_CLIENT_ID,
				} as AifcExperienceTrackingCommandProperties;
				break;
		}

		void api?.send(AifcExperienceTrackingCommand, payload);
	} catch (error) {
		// We don't want to throw an error here, just stop it from bubbling up since this is not critical
		getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
	}
};

export const useRovoBridgeCommandListener = ({
	editorAPI,
	onTitleChange,
	contentId,
	setEditorDisabled,
	forceDraftSync,
	generateAIHeaderImage,
	fakeGenerateAIHeaderImage,
	contentContext,
	mediaProvider,
}: UseRovoBridgeCommandListenerParams) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const isViewMode = useIsPageViewMode();

	const imageProcessor = useMediaImageProcessor({
		mediaProvider,
	});

	const reuploadImages = useCallback(async () => {
		if (!imageProcessor?.processImagesInAdf || !fg('confluence_aifc_image_reuploading')) {
			return;
		}

		if (!editorAPI) {
			return;
		}

		const adfDoc = await getDocument(editorAPI);

		if (!adfDoc) {
			return;
		}

		// Process images in the background and update when ready
		imageProcessor
			.processImagesInAdf(adfDoc)
			.then(({ error, hasChanges, processedAdf }) => {
				if (error) {
					getMonitoringClient().submitError(error, {
						attribution: Attribution.PAGE_MODES,
					});
				} else if (hasChanges) {
					editorAPI?.core.actions.replaceDocument(processedAdf, {
						scrollIntoView: false,
					});
				}
			})
			.catch((error) => {
				// Editor already has the original content, so this is non-fatal
				getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
			});
	}, [imageProcessor, editorAPI]);

	const sendInitialCreationAnalytics = useCallback(async () => {
		try {
			let features;
			if (editorAPI) {
				const doc = await getDocument(editorAPI, true);
				if (doc) {
					features = extractContentFeatures(doc);
				}
			}

			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'smartCreateContent',
				action: 'initialContentCreated',
				attributes: {
					createdContentProduct: 'confluence',
					createdContentType: 'page',
					createdContentFeatures: features,
					createdContentMetadata: {
						contentId,
					},
				},
			});
		} catch (error) {
			getMonitoringClient().submitError(error, { attribution: Attribution.PAGE_MODES });
		}
	}, [editorAPI, contentId, sendAnalyticsEvent]);

	// Called once when streaming starts
	const onStreamStart = useCallback(
		({ streamType }: StreamStartCallbackParams) => {
			setEditorDisabled(true);

			if (streamType === 'create' || streamType === 'deep-research-create') {
				if (fg('confluence_aifc_image_generation')) {
					if (!fg('aifc_header_image_pseudo_title_enabled')) {
						void fakeGenerateAIHeaderImage?.();
					}
				}
			}
		},
		[setEditorDisabled, fakeGenerateAIHeaderImage],
	);

	// Called on every stream chunk
	const onStream: StreamCallback = useCallback(
		({ params }: StreamCallbackParams) => {
			if ('title' in params.data) {
				onTitleChange?.(params.data.title);
			}
		},
		[onTitleChange],
	);

	// Called when streaming ends
	const onStreamEnd = useCallback(
		({ streamType, params }: StreamEndCallbackParams) => {
			try {
				if (streamType !== 'update') {
					void reuploadImages();
				}

				if (streamType === 'create') {
					const title = 'title' in params?.data ? params.data.title : undefined;

					if (title) {
						if (fg('confluence_aifc_image_generation')) {
							if (!fg('aifc_header_image_pseudo_title_enabled')) {
								void generateAIHeaderImage?.({ adfContent: title });
							}
						}
					}

					const SENDER_ID = getConfluenceEditorClientId(contentId);
					void sendExperienceTrackingToParent(
						{
							status: 'success',
							actionType: 'page',
							actionId: contentId,
							contentContext,
							experienceName: RovoExperienceName.AIFC_GENERATE_CONTENT,
						},
						{ senderId: SENDER_ID },
					);

					void sendInitialCreationAnalytics();
				}
			} finally {
				setEditorDisabled(false);
			}
		},
		[
			contentId,
			contentContext,
			setEditorDisabled,
			reuploadImages,
			generateAIHeaderImage,
			sendInitialCreationAnalytics,
		],
	);

	const onStreamError = useCallback(
		({ error, streamType }: ErrorCallbackParams) => {
			log('Error in command handler:', error);

			if (streamType === 'create') {
				const SENDER_ID = getConfluenceEditorClientId(contentId);
				void sendExperienceTrackingToParent(
					{
						status: 'fail',
						actionType: 'page',
						actionId: contentId,
						contentContext,
						experienceName: RovoExperienceName.AIFC_GENERATE_CONTENT,
						error: error instanceof Error ? error.message : String(error),
					},
					{ senderId: SENDER_ID },
				);
			}
		},
		[contentId, contentContext],
	);

	const listener = useMemo(() => {
		if (!editorAPI) {
			return null;
		}

		return new RovoBridgeCommandManager(contentId, contentContext, editorAPI);
	}, [contentId, contentContext, editorAPI]);

	useEffect(() => {
		if (!listener || !editorAPI) {
			return;
		}

		const commandProperties: PassThroughCommandProperties = {
			editorAPI,
			contentId,
			onStreamStart,
			onStream,
			onStreamEnd,
			onStreamError,
			forceDraftSync,
			generateAIHeaderImage,
			setEditorDisabled,
			isViewMode,
		};
		listener.registerCommands(commandProperties);

		return () => {
			listener.deregisterCommands();
		};
	}, [
		listener,
		editorAPI,
		contentId,
		forceDraftSync,
		onStreamStart,
		onStream,
		onStreamEnd,
		onStreamError,
		generateAIHeaderImage,
		setEditorDisabled,
		isViewMode,
	]);

	useEffect(() => {
		if (!listener) {
			return;
		}

		listener.ready();
		scheduleTeardown(listener.teardown.bind(listener));

		return () => {
			try {
				listener.teardown();
				// TODO: clear inactivity timeouts?
			} catch {
				// TODO - handle errors; should go to Sentry
				// could be sent in future by navigation lockout
			}
		};
	}, [listener]);
};
